/**
 * Centralized Time Formatting Utilities
 * All time displays should use 24-hour format (HH:mm) consistently.
 * 
 * Usage:
 *   import { formatTime24, formatDateShort, formatDateTime24 } from '../lib/timeformat';
 *   
 *   formatTime24(new Date())           // "14:30"
 *   formatTime24("2026-02-28T18:00:00") // "18:00"
 *   formatDateShort(new Date())        // "Sat 28 Feb"
 *   formatDateTime24(new Date())       // "Sat 28 Feb 14:30"
 */

const DEFAULT_TIMEZONE = 'Australia/Sydney';

/**
 * Format time in 24-hour format (HH:mm)
 * @param {Date|string|number} dt - Date object, ISO string, or timestamp
 * @param {string} tz - Timezone (default: Australia/Sydney)
 * @returns {string} Time in "HH:mm" format, or "-" if invalid
 */
export function formatTime24(dt, tz = DEFAULT_TIMEZONE) {
  if (!dt) return '-';
  
  try {
    const date = dt instanceof Date ? dt : new Date(dt);
    if (isNaN(date.getTime())) return '-';
    
    return date.toLocaleTimeString('en-AU', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: tz
    });
  } catch (error) {
    console.warn('[timeformat] Invalid date:', dt, error);
    return '-';
  }
}

/**
 * Format time range in 24-hour format (HH:mm - HH:mm)
 * @param {Date|string} start - Start time
 * @param {Date|string} end - End time
 * @param {string} tz - Timezone
 * @returns {string} Time range like "08:00 - 18:00"
 */
export function formatTimeRange24(start, end, tz = DEFAULT_TIMEZONE) {
  const startStr = formatTime24(start, tz);
  const endStr = formatTime24(end, tz);
  return `${startStr} - ${endStr}`;
}

/**
 * Format date in short format (Mon 16 Feb)
 * @param {Date|string|number} dt - Date object, ISO string, or timestamp
 * @param {string} tz - Timezone
 * @returns {string} Date in "Mon 16 Feb" format
 */
export function formatDateShort(dt, tz = DEFAULT_TIMEZONE) {
  if (!dt) return '-';
  
  try {
    const date = dt instanceof Date ? dt : new Date(dt);
    if (isNaN(date.getTime())) return '-';
    
    return date.toLocaleDateString('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      timeZone: tz
    });
  } catch (error) {
    console.warn('[timeformat] Invalid date:', dt, error);
    return '-';
  }
}

/**
 * Format date in numeric format (28/02/2026)
 * @param {Date|string|number} dt - Date object, ISO string, or timestamp
 * @param {string} tz - Timezone
 * @returns {string} Date in "DD/MM/YYYY" format
 */
export function formatDateNumeric(dt, tz = DEFAULT_TIMEZONE) {
  if (!dt) return '-';
  
  try {
    const date = dt instanceof Date ? dt : new Date(dt);
    if (isNaN(date.getTime())) return '-';
    
    return date.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: tz
    });
  } catch (error) {
    return '-';
  }
}

/**
 * Format full datetime in 24-hour format (Sat 28 Feb 14:30)
 * @param {Date|string|number} dt - Date object, ISO string, or timestamp
 * @param {string} tz - Timezone
 * @returns {string} Datetime in "Mon 16 Feb 14:30" format
 */
export function formatDateTime24(dt, tz = DEFAULT_TIMEZONE) {
  if (!dt) return '-';
  
  try {
    const date = dt instanceof Date ? dt : new Date(dt);
    if (isNaN(date.getTime())) return '-';
    
    const dateStr = formatDateShort(date, tz);
    const timeStr = formatTime24(date, tz);
    return `${dateStr} ${timeStr}`;
  } catch (error) {
    return '-';
  }
}

/**
 * Format duration in hours and minutes
 * @param {number} minutes - Duration in minutes
 * @returns {string} Duration like "8h 30m" or "45m"
 */
export function formatDuration(minutes) {
  if (!minutes || minutes < 0) return '-';
  
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

/**
 * Format hours as decimal (for payroll/timesheets)
 * @param {number} hours - Hours as decimal
 * @param {number} decimals - Decimal places (default: 2)
 * @returns {string} Formatted hours like "8.50"
 */
export function formatHoursDecimal(hours, decimals = 2) {
  if (hours == null || isNaN(hours)) return '-';
  return hours.toFixed(decimals);
}

/**
 * Parse time string to 24-hour format
 * Accepts: "1800", "18:00", "6pm", "6:00 PM", "18:00:00"
 * @param {string} timeStr - Time string to parse
 * @returns {string|null} Time in "HH:mm" format or null if invalid
 */
export function parseTimeTo24(timeStr) {
  if (!timeStr) return null;
  
  const clean = String(timeStr).trim().toUpperCase();
  
  // Already HH:mm format
  const hhmm = clean.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (hhmm) {
    const h = parseInt(hhmm[1], 10);
    const m = parseInt(hhmm[2], 10);
    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
      return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    }
  }
  
  // Compact format: 1800, 800
  const compact = clean.match(/^(\d{3,4})$/);
  if (compact) {
    const num = compact[1].padStart(4, '0');
    const h = parseInt(num.slice(0, 2), 10);
    const m = parseInt(num.slice(2, 4), 10);
    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
      return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    }
  }
  
  // AM/PM format: 6PM, 6:00PM, 6:00 PM
  const ampm = clean.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/);
  if (ampm) {
    let h = parseInt(ampm[1], 10);
    const m = parseInt(ampm[2] || '0', 10);
    const period = ampm[3];
    
    if (period === 'PM' && h < 12) h += 12;
    if (period === 'AM' && h === 12) h = 0;
    
    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
      return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    }
  }
  
  return null;
}

/**
 * Parse a date key string (YYYY-MM-DD) to a local Date object
 * Avoids timezone shift issues when displaying dates in UI
 * 
 * IMPORTANT: new Date("2026-02-16") creates midnight UTC, which shifts
 * to the previous day in positive timezone offsets (e.g., Australia).
 * This function creates a local date without timezone conversion.
 * 
 * @param {string} dateKey - Date string in YYYY-MM-DD format
 * @returns {Date} Local Date object representing that calendar date
 */
export function parseDateKey(dateKey) {
  if (!dateKey) return new Date();
  
  const parts = dateKey.split('-');
  if (parts.length !== 3) {
    // Fallback: try parsing normally
    return new Date(dateKey);
  }
  
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // JS months are 0-indexed
  const day = parseInt(parts[2], 10);
  
  return new Date(year, month, day);
}

/**
 * Format a date key (YYYY-MM-DD) for display without timezone issues
 * @param {string} dateKey - Date string in YYYY-MM-DD format
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export function formatDateKey(dateKey, options = { weekday: 'short', day: 'numeric', month: 'short' }) {
  const date = parseDateKey(dateKey);
  return date.toLocaleDateString('en-AU', options);
}

export default {
  formatTime24,
  formatTimeRange24,
  formatDateShort,
  formatDateNumeric,
  formatDateTime24,
  formatDuration,
  formatHoursDecimal,
  parseTimeTo24,
  parseDateKey,
  formatDateKey
};
